export type OrgWorkloadWindowGranularity = 'point' | 'week' | 'month' | 'custom';

export interface OrgWorkloadLensSettings {
  readonly granularity: OrgWorkloadWindowGranularity;
  readonly customStartDate: string | null;
  readonly customEndDate: string | null;
  readonly targetKinds: readonly string[];
  readonly targetExternalIds: readonly string[];
  readonly sourceKeys: readonly string[];
  readonly candidateTargetKind: string | null;
  readonly candidateTargetExternalId: string | null;
  readonly candidateTargetLabel: string | null;
  readonly candidateProposedAllocation: number | null;
}

export interface OrgWorkloadResolvedWindow {
  readonly granularity: OrgWorkloadWindowGranularity;
  readonly start: Date | null;
  readonly end: Date | null;
  readonly label: string;
}

export const DEFAULT_ORG_WORKLOAD_LENS_SETTINGS: OrgWorkloadLensSettings = {
  granularity: 'point',
  customStartDate: null,
  customEndDate: null,
  targetKinds: [],
  targetExternalIds: [],
  sourceKeys: [],
  candidateTargetKind: null,
  candidateTargetExternalId: null,
  candidateTargetLabel: null,
  candidateProposedAllocation: null,
};

const DAY_MS = 24 * 60 * 60 * 1000;

export function resolveOrgWorkloadWindow(
  settings: OrgWorkloadLensSettings,
  referenceValue?: Date | string | null,
): OrgWorkloadResolvedWindow {
  const reference = parseOrgWorkloadDate(referenceValue) ?? startOfLocalDay(new Date());

  if (settings.granularity === 'week') {
    const start = startOfWeek(reference);
    const end = addDays(start, 6);
    return {
      granularity: 'week',
      start,
      end,
      label: `Week of ${formatShortDate(start)}`,
    };
  }

  if (settings.granularity === 'month') {
    const start = new Date(reference.getFullYear(), reference.getMonth(), 1);
    const end = new Date(reference.getFullYear(), reference.getMonth() + 1, 0);
    return {
      granularity: 'month',
      start,
      end,
      label: formatMonthYear(start),
    };
  }

  if (settings.granularity === 'custom') {
    const parsedStart = parseOrgWorkloadDate(settings.customStartDate);
    const parsedEnd = parseOrgWorkloadDate(settings.customEndDate);
    if (parsedStart && parsedEnd) {
      const start = parsedStart.getTime() <= parsedEnd.getTime() ? parsedStart : parsedEnd;
      const end = parsedStart.getTime() <= parsedEnd.getTime() ? parsedEnd : parsedStart;
      return {
        granularity: 'custom',
        start,
        end,
        label: `${formatShortDate(start)} - ${formatShortDate(end)}`,
      };
    }
  }

  return {
    granularity: 'point',
    start: null,
    end: null,
    label: `As of ${formatShortDate(reference)}`,
  };
}

export function parseOrgWorkloadDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return startOfLocalDay(value);
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : startOfLocalDay(parsed);
}

export function formatOrgWorkloadDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function normalizeOrgWorkloadFilterKey(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

export function orgWorkloadWindowDayCount(window: OrgWorkloadResolvedWindow): number {
  if (!window.start || !window.end) return 1;
  return Math.max(1, Math.round((exclusiveEnd(window.end).getTime() - window.start.getTime()) / DAY_MS));
}

export function orgWorkloadWindowOverlapRatio(
  window: OrgWorkloadResolvedWindow,
  intervalStart: Date | null,
  intervalEnd: Date | null,
): number {
  if (!window.start || !window.end) return 1;

  const windowStart = window.start.getTime();
  const windowEnd = exclusiveEnd(window.end).getTime();
  const start = intervalStart ? Math.max(windowStart, intervalStart.getTime()) : windowStart;
  const end = intervalEnd ? Math.min(windowEnd, exclusiveEnd(intervalEnd).getTime()) : windowEnd;
  if (end <= start) return 0;

  return Math.max(0, Math.min(1, (end - start) / (windowEnd - windowStart)));
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function startOfWeek(date: Date): Date {
  const start = startOfLocalDay(date);
  const mondayOffset = (start.getDay() + 6) % 7;
  return addDays(start, -mondayOffset);
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * DAY_MS);
}

function exclusiveEnd(date: Date): Date {
  return addDays(startOfLocalDay(date), 1);
}

function formatShortDate(date: Date): string {
  return new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(date);
}

function formatMonthYear(date: Date): string {
  return new Intl.DateTimeFormat('en', { month: 'short', year: 'numeric' }).format(date);
}
